import { resolveMeterColor } from "@next-platform/client-common-core";
import { boundedMeterFill } from "@next-platform/client-common-core/rulebook/meterFill";
import formatMeterStartValue, { meterStartLevelCaption } from "@next-platform/client-common-core/rulebook/profile/formatMeterStartValue";
import { IconSize } from "@next-platform/client-common-spicy-ui";
import { type MeterDefinition, MeterType } from "@next-platform/packages-ts-types";
import { memo } from "react";

import { Col } from "@/components";
import RulebookMeterBar from "@/components/Rulebook/RulebookMeterBar";
import RulebookMeterIcon from "@/components/Rulebook/RulebookMeterIcon";
import RulebookTokenText from "@/components/Rulebook/RulebookTokenText";
import type { RenderTokenChip } from "@/components/Rulebook/tokenChipTint";
import Trans from "@/components/Trans/Trans";
import { useMeterColorTheme } from "@/hooks";

import RulebookProfileSectionHeading from "./RulebookProfileSectionHeading";
import useProfileTokenRenderer from "./useProfileTokenRenderer";

const StartValueTile = ({ meter, renderChip }: { meter: MeterDefinition; renderChip: RenderTokenChip }) => {
  const theme = useMeterColorTheme();
  const iconColor = resolveMeterColor({ color: meter.color, theme });
  const caption = meterStartLevelCaption(meter);
  const startValue = formatMeterStartValue(meter);
  const shouldShowBar = meter.type === MeterType.BAR || meter.type === MeterType.PIPS;

  return (
    <div className="flex flex-col gap-1.5 rounded-lg bg-gray-2 p-2.5" data-testid={`RulebookProfile-StartTile-${meter.id}`}>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-11">
        <RulebookMeterIcon icon={meter.icon} meterType={meter.type} color={iconColor} size={IconSize.XSMALL} />
        <span className="truncate">{meter.label}</span>
      </div>
      <span className="text-xl font-bold leading-none tabular-nums">{startValue}</span>
      {shouldShowBar ? (
        <RulebookMeterBar
          fill={boundedMeterFill(meter, meter.default)}
          color={iconColor}
          size="tile"
          data-testid={`RulebookProfile-StartTile-${meter.id}-Bar`}
          fillTestId={`RulebookProfile-StartTile-${meter.id}-Bar-Fill`}
        />
      ) : null}
      {caption ? (
        <span className="truncate text-xs text-gray-11">
          <RulebookTokenText text={caption} renderChip={renderChip} />
        </span>
      ) : null}
    </div>
  );
};

type RulebookStartValuesSectionProps = {
  meters: MeterDefinition[];
};

/**
 * "What you'll see in chat" — one start-value tile per user-visible meter. Only rendered when the
 * rulebook shares its full definition; hidden meters never get a tile.
 */
const RulebookStartValuesSection = ({ meters }: RulebookStartValuesSectionProps) => {
  const visibleMeters = meters.filter((meter) => meter.visible !== false);
  const renderChip = useProfileTokenRenderer({ definitionMeters: meters });

  if (visibleMeters.length === 0) {
    return null;
  }

  return (
    <Col className="gap-3" dataTestId="RulebookProfile-WhatYouSee">
      <RulebookProfileSectionHeading
        title={<Trans i18nKey="rulebook:profile.whatYouSee.title" />}
        subtitle={<Trans i18nKey="rulebook:profile.whatYouSee.subtitle" />}
      />
      <div className="grid max-w-[680px] grid-cols-2 gap-2 rounded-[14px] border border-solid border-gray-5 dark:border-gray-8 bg-gray-2 p-2 md:grid-cols-4">
        {visibleMeters.map((meter) => (
          <StartValueTile key={meter.id} meter={meter} renderChip={renderChip} />
        ))}
      </div>
    </Col>
  );
};

export default memo(RulebookStartValuesSection);
